import { useCallback } from 'react';
import { useNullableSideBySideApi, useSideBySideIndex } from '@/components/SideBySide';
import { useSpace } from '@/components/SpaceProvider';
import { usePerformBlockChangeWithEditor } from '@/components/RealtimeDocumentProvider';
import { SagaLocation, Converter, AnyBlockItem, SagaElement } from '@saga/shared';
import { Transforms, Editor } from 'slate';

// DoS Protection: Maximum number of top-level blocks to add
const MAX_BLOCKS = 750;

/**
 * IMPORTANT: This operation does NOT support undo/redo. The changeBlocksWithEditor uses a different
 * origin that bypasses the undo stack to prevent race conditions in collaborative editing.
 * The confirmation dialog serves as the safety mechanism for this destructive operation.
 */
export function useRewritePageContent() {
    const changeBlocksWithEditor = usePerformBlockChangeWithEditor();
    const sideBySideApi = useNullableSideBySideApi();
    const currentPaneIndex = useSideBySideIndex();
    const { space } = useSpace();

    const rewritePageContent = useCallback(
        async (pageId: string, content: string): Promise<{ success: boolean; error?: string }> => {
            try {
                // Parse the content from XML
                const elements = Converter.xmlToSaga(content) as SagaElement[];
                if (!elements || elements.length === 0) {
                    return {
                        success: false,
                        error: 'No valid content to rewrite',
                    };
                }

                // Filter out title elements as they are not AnyBlockItem
                // This prevents the AI from accidentally changing the page title
                const blocks = elements.filter((el): el is AnyBlockItem => el.type !== 'title');

                // Check if we have any content blocks after filtering
                if (blocks.length === 0) {
                    return {
                        success: false,
                        error: 'No valid content blocks to insert (title tags are not allowed)',
                    };
                }

                // DoS Protection: Limit number of blocks
                if (blocks.length > MAX_BLOCKS) {
                    return {
                        success: false,
                        error: `Content exceeds block limit (${blocks.length} > ${MAX_BLOCKS})`,
                    };
                }

                // Create the page location
                const location: SagaLocation.PageLocation = {
                    type: 'page',
                    pageId,
                    blockId: undefined,
                };

                // Use changeBlocksWithEditor to replace all content except the title
                // Note: This operation bypasses the undo stack by design (see function docs above)
                await changeBlocksWithEditor(space, location, (editor) => {
                    // Perform delete and insert as atomic operations to prevent race conditions
                    Editor.withoutNormalizing(editor, () => {
                        // Preserve the title block (always at index 0) and delete everything else
                        if (editor.children.length > 1) {
                            // Remove all nodes starting from the second node (index 1) to the end.
                            Transforms.removeNodes(editor, {
                                at: {
                                    anchor: Editor.start(editor, [1]),
                                    focus: Editor.end(editor, [editor.children.length - 1]),
                                },
                            });
                        }

                        // Insert the new blocks after the title block
                        if (blocks.length > 0) {
                            Transforms.insertNodes(editor, blocks, { at: [1] });
                        }
                    });

                    // Force normalization after the operation
                    Editor.normalize(editor, { force: true });
                });

                // Open the page in the side-by-side view if available
                if (sideBySideApi) {
                    const targetIndex = currentPaneIndex === 0 ? 1 : 0;
                    sideBySideApi.openLocationOnIndex(SagaLocation.pageLocationFromId(pageId), targetIndex);
                }

                return { success: true };
            } catch (error) {
                console.error('Failed to rewrite page content:', error);
                return {
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to rewrite page content',
                };
            }
        },
        [changeBlocksWithEditor, sideBySideApi, currentPaneIndex, space],
    );

    return { rewritePageContent };
}
